import { ZONE_IDS, ZoneId, zoneIdForTarget } from '../../helpers/goalTargetFrame';
import { POOL_ZONE_IDS, PoolZoneId, poolZoneIdForLocation } from '../../helpers/poolShotZones';
import { EventLightVm, EventShotLightVm, MatchPlayerVm } from '../../utils/api';
import {
    buildScoreAtMomentByEventId,
    emptyMapSide,
    mapPointContext,
    MapSide,
    matchFinalScoreLine,
} from './insightsMapData';
import {
    MapPeriodKey,
    matchesPeriodFilter,
    nameByMatchPlayerId,
    playerDisplayName,
    sortEventsChronological,
} from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';
import { matchesShotKind, ShotKindFilter } from './insightsShotKindFilters';
import { AttackType, classifyShot } from './insightsTeamSplits';

/*
 * Goalkeeper stats aligned with total-waterpolo-server process-events.ts:
 * - Saves always from shot.savedById
 * - Goals against from active defending GK when the team has a Starting GK event
 *   (Goalkeeper_Change with previous == null); else legacy last-saver / pending
 * - Numbered shootout rows also use active-GK stamps without a Starting GK marker.
 * - attempts (SOG) = saves + goals against only (blocks/misses never count)
 * Zones are an Insights overlay on top of that attribution - not in the WP server.
 */

export const GK_SAMPLE_WARNING_SOG = 8;

/** Situation filters for GK maps on the player page. */
export type GkSituation = 'all' | 'extraPlayer' | 'sixOnSix' | 'penalty';

/** Multi-select chips (All together is a shortcut, not a chip value). */
export type GkSituationKey = 'extraPlayer' | 'sixOnSix' | 'penalty';

export const GK_SITUATION_KEYS: GkSituationKey[] = ['extraPlayer', 'sixOnSix', 'penalty'];

export const GK_SITUATION_LABELS: Record<GkSituation, string> = {
    all: 'All together',
    extraPlayer: 'Extra player',
    sixOnSix: '6 to 6',
    penalty: 'Penalties',
};

export const matchesGkSituation = (shot: EventShotLightVm, situation?: GkSituation): boolean => {
    if (!situation || situation === 'all') return true;
    const type = classifyShot(shot);
    if (situation === 'sixOnSix') return type === 'regular' || type === 'counter';
    return type === situation;
};

/** Empty or all three chips = no filter (same as All together). */
export const isAllSituations = (selected: GkSituationKey[]): boolean =>
    selected.length === GK_SITUATION_KEYS.length;

export const matchesSelectedSituations = (
    shot: EventShotLightVm,
    selected: GkSituationKey[]
): boolean => {
    if (selected.length === 0) return false;
    if (isAllSituations(selected)) return true;
    return selected.some((key) => matchesGkSituation(shot, key));
};

/**
 * Chip toggle: from All → solo that key; otherwise add/remove.
 * Empty selection snaps back to all three (avoid blank maps).
 */
export const toggleSituationKey = (
    selected: GkSituationKey[],
    key: GkSituationKey
): GkSituationKey[] => {
    if (isAllSituations(selected)) return [key];
    if (selected.includes(key)) {
        const next = selected.filter((s) => s !== key);
        return next.length === 0 ? [...GK_SITUATION_KEYS] : next;
    }
    return [...selected, key];
};

export interface ZoneCell {
    zone: ZoneId;
    /** Attributed attempts in this zone (saves + goals). */
    shots: number;
    goals: number;
    saves: number;
}

export interface PoolZoneCell {
    zone: PoolZoneId;
    shots: number;
    goals: number;
    saves: number;
}

export interface GkZoneRow {
    matchPlayerIds: number[];
    playerId?: number;
    name: string;
    cells: ZoneCell[];
    poolCells: PoolZoneCell[];
    /** attempts = saves + goalsAgainst (TW SaveStat.attempts). */
    sog: number;
    saves: number;
    goalsAgainst: number;
    savePct?: number;
    sampleWarning: boolean;
    hottestZone?: ZoneId;
    located: number;
    total: number;
}

export interface GkZoneStats {
    teamAgainst: ZoneCell[];
    teamAgainstPool: PoolZoneCell[];
    keepers: GkZoneRow[];
    located: number;
    total: number;
    offTarget: number;
    noTarget: number;
}

export type ZoneCounts = Partial<Record<string, { goals: number; saves: number; shots: number }>>;

export interface CollectGkZoneOptions {
    /** @deprecated prefer situation */
    attackType?: AttackType;
    situation?: GkSituation;
    /** Multi-select situations; unions shots. Wins over single `situation` when set. */
    situations?: GkSituationKey[];
    playerId?: number;
    /** null = all periods; [] = none. */
    periodKeys?: MapPeriodKey[] | null;
    /** Technique / direct-foul / fakes. null dims = all. */
    kind?: ShotKindFilter;
}

export interface GkMapOptions {
    situation?: GkSituation;
    /** Multi-select situations; unions shots. Wins over single `situation` when set. */
    situations?: GkSituationKey[];
    /** When true, only goals against (penalty “where scored” focus). */
    goalsOnly?: boolean;
    /** null = all periods; [] = none. */
    periodKeys?: MapPeriodKey[] | null;
    /** Technique / direct-foul / fakes. null dims = all. */
    kind?: ShotKindFilter;
}

const emptyCell = (zone: ZoneId): ZoneCell => ({
    zone,
    shots: 0,
    goals: 0,
    saves: 0,
});

const emptyCells = (): ZoneCell[] => ZONE_IDS.map(emptyCell);

const emptyPoolCell = (zone: PoolZoneId): PoolZoneCell => ({
    zone,
    shots: 0,
    goals: 0,
    saves: 0,
});

const emptyPoolCells = (): PoolZoneCell[] => POOL_ZONE_IDS.map(emptyPoolCell);

const bumpCell = (cells: ZoneCell[], zone: ZoneId, kind: 'goal' | 'saved'): void => {
    const cell = cells.find((c) => c.zone === zone);
    if (!cell) return;
    cell.shots += 1;
    if (kind === 'goal') cell.goals += 1;
    else cell.saves += 1;
};

const bumpPoolCell = (cells: PoolZoneCell[], zone: PoolZoneId, kind: 'goal' | 'saved'): void => {
    const cell = cells.find((c) => c.zone === zone);
    if (!cell) return;
    cell.shots += 1;
    if (kind === 'goal') cell.goals += 1;
    else cell.saves += 1;
};

type LineupRow = { playerId: number; name: string; teamId: number };

const indexLineup = (bundle: MatchBundle): Map<number, LineupRow> => {
    const byMatchPlayerId = new Map<number, LineupRow>();
    (bundle.detail.players || []).forEach((mp: MatchPlayerVm) => {
        if (mp.id == null || mp.playerId == null || mp.teamId == null) return;
        byMatchPlayerId.set(mp.id, {
            playerId: mp.playerId,
            name: playerDisplayName(mp),
            teamId: mp.teamId,
        });
    });
    return byMatchPlayerId;
};

/** Team has Starting GK event → Arena 1.1.5+ active-GK fields are trustworthy. */
export const detectNewGkTrackingByTeam = (events: EventLightVm[]): Map<number, boolean> => {
    const byTeam = new Map<number, boolean>();
    events.forEach((event) => {
        const change = event.goalkeeperChange;
        if (!change) return;
        if (change.previousGoalkeeperMatchPlayerId == null) {
            byTeam.set(change.teamId, true);
        }
    });
    return byTeam;
};

/** Opposite side's active GK match-player id for this shot (TW resolveDefendingGk). */
export const resolveDefendingGkMatchPlayerId = (
    event: EventLightVm,
    homeTeamId: number,
    awayTeamId: number
): number | undefined => {
    const shootingTeamId = event.shot?.teamId;
    if (shootingTeamId == null) return undefined;
    if (shootingTeamId === homeTeamId) {
        return event.activeAwayGoalkeeperMatchPlayerId ?? undefined;
    }
    if (shootingTeamId === awayTeamId) {
        return event.activeHomeGoalkeeperMatchPlayerId ?? undefined;
    }
    return undefined;
};

interface KeeperAccum {
    matchPlayerIds: Set<number>;
    playerId?: number;
    name: string;
    cells: ZoneCell[];
    poolCells: PoolZoneCell[];
    sog: number;
    saves: number;
    goalsAgainst: number;
    located: number;
    total: number;
}

const finalizeKeeper = (acc: KeeperAccum): GkZoneRow => {
    const savePct =
        acc.saves + acc.goalsAgainst > 0
            ? Math.round((acc.saves / (acc.saves + acc.goalsAgainst)) * 100)
            : undefined;
    let hottestZone: ZoneId | undefined;
    let hottestGoals = 0;
    acc.cells.forEach((cell) => {
        if (cell.goals > hottestGoals) {
            hottestGoals = cell.goals;
            hottestZone = cell.zone;
        }
    });
    return {
        matchPlayerIds: Array.from(acc.matchPlayerIds),
        playerId: acc.playerId,
        name: acc.name,
        cells: acc.cells,
        poolCells: acc.poolCells,
        sog: acc.sog,
        saves: acc.saves,
        goalsAgainst: acc.goalsAgainst,
        savePct,
        sampleWarning: acc.sog < GK_SAMPLE_WARNING_SOG,
        hottestZone,
        located: acc.located,
        total: acc.total,
    };
};

export const cellsToZoneCounts = (
    cells: Array<{ zone: string; shots: number; goals: number; saves: number }>
): ZoneCounts => {
    const result: ZoneCounts = {};
    cells.forEach((cell) => {
        if (cell.shots === 0) return;
        result[cell.zone] = { goals: cell.goals, saves: cell.saves, shots: cell.shots };
    });
    return result;
};

export const hottestZoneLabel = (zone?: ZoneId): string => (zone ? zone : '-');

export const savePctText = (saves: number, goalsAgainst: number): string =>
    saves + goalsAgainst > 0 ? `${Math.round((saves / (saves + goalsAgainst)) * 100)}%` : '-';

export const cellSavePctText = (saves: number, goals: number): string =>
    saves + goals > 0 ? `${Math.round((saves / (saves + goals)) * 100)}%` : '-';

const shotPassesFilters = (
    shot: EventShotLightVm,
    options: CollectGkZoneOptions | GkMapOptions
): boolean => {
    if ('attackType' in options && options.attackType) {
        if (classifyShot(shot) !== options.attackType) return false;
    }
    if ('situations' in options && options.situations) {
        if (!matchesSelectedSituations(shot, options.situations)) return false;
    } else if ('situation' in options && options.situation) {
        if (!matchesGkSituation(shot, options.situation)) return false;
    }
    if ('goalsOnly' in options && options.goalsOnly && !shot.isGoal) return false;
    if ('kind' in options && options.kind && !matchesShotKind(shot, options.kind)) return false;
    return true;
};

/**
 * Aggregate shots against our club into team + per-GK zone tables (TW attribution).
 */
export const collectGkZoneStats = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    options: CollectGkZoneOptions = {}
): GkZoneStats => {
    const teamAgainst = emptyCells();
    const teamAgainstPool = emptyPoolCells();
    const keepers = new Map<string, KeeperAccum>();
    let located = 0;
    let total = 0;
    let offTarget = 0;
    let noTarget = 0;

    const ensureKeeper = (
        key: string,
        seed: { matchPlayerId?: number; playerId?: number; name: string }
    ): KeeperAccum => {
        let acc = keepers.get(key);
        if (!acc) {
            acc = {
                matchPlayerIds: new Set(),
                playerId: seed.playerId,
                name: seed.name,
                cells: emptyCells(),
                poolCells: emptyPoolCells(),
                sog: 0,
                saves: 0,
                goalsAgainst: 0,
                located: 0,
                total: 0,
            };
            keepers.set(key, acc);
        }
        if (seed.matchPlayerId != null) acc.matchPlayerIds.add(seed.matchPlayerId);
        if (seed.playerId != null) acc.playerId = seed.playerId;
        return acc;
    };

    type PendingZone = {
        goalZone: ZoneId | 'offTarget' | null;
        poolZone: PoolZoneId | 'deep' | null;
    };

    const credit = (
        lineupRow: LineupRow,
        matchPlayerId: number | undefined,
        kind: 'goal' | 'saved',
        goalZone: ZoneId | 'offTarget' | null,
        poolZone: PoolZoneId | 'deep' | null
    ): void => {
        if (!ourTeamIds.includes(lineupRow.teamId)) return;
        if (options.playerId != null && lineupRow.playerId !== options.playerId) return;

        total += 1;
        if (goalZone == null) noTarget += 1;
        else if (goalZone === 'offTarget') offTarget += 1;
        else {
            located += 1;
            bumpCell(teamAgainst, goalZone, kind);
        }
        if (poolZone != null && poolZone !== 'deep') {
            bumpPoolCell(teamAgainstPool, poolZone, kind);
        }

        const acc = ensureKeeper(String(lineupRow.playerId), {
            matchPlayerId,
            playerId: lineupRow.playerId,
            name: lineupRow.name,
        });
        acc.total += 1;
        acc.sog += 1;
        if (kind === 'saved') acc.saves += 1;
        else acc.goalsAgainst += 1;
        if (goalZone != null && goalZone !== 'offTarget') {
            acc.located += 1;
            bumpCell(acc.cells, goalZone, kind);
        }
        if (poolZone != null && poolZone !== 'deep') {
            bumpPoolCell(acc.poolCells, poolZone, kind);
        }
    };

    bundles.forEach((bundle) => {
        const lineup = indexLineup(bundle);
        const newTracking = detectNewGkTrackingByTeam(bundle.events);
        // Legacy: last saver match-player id per defending team.
        const lastSaverByTeam = new Map<number, number>();
        // Goals waiting for first save on that defending team (legacy only).
        const pendingGoalsByTeam = new Map<number, PendingZone[]>();

        const flushPending = (teamId: number, saverMpId: number): void => {
            const pending = pendingGoalsByTeam.get(teamId);
            if (!pending || pending.length === 0) return;
            const saver = lineup.get(saverMpId);
            if (!saver) {
                pendingGoalsByTeam.delete(teamId);
                return;
            }
            pending.forEach((zones) =>
                credit(saver, saverMpId, 'goal', zones.goalZone, zones.poolZone)
            );
            pendingGoalsByTeam.delete(teamId);
        };

        sortEventsChronological(bundle.events).forEach((event: EventLightVm) => {
            if (!event.shot) return;
            if (ourTeamIds.includes(event.shot.teamId)) return;
            if (!matchesPeriodFilter(event, options.periodKeys)) return;
            if (!shotPassesFilters(event.shot, options)) return;

            const goalZone = zoneIdForTarget(event.shot.targetX, event.shot.targetY);
            const poolZone = poolZoneIdForLocation(event.shot.locationX, event.shot.locationY);
            const isGoal = !!event.shot.isGoal;
            const savedById = event.shot.savedById;

            // --- Save: always savedBy (TW) ---
            if (!isGoal && savedById != null) {
                const saver = lineup.get(savedById);
                if (saver) {
                    flushPending(saver.teamId, savedById);
                    lastSaverByTeam.set(saver.teamId, savedById);
                    credit(saver, savedById, 'saved', goalZone, poolZone);
                }
                return;
            }

            // --- Goal against ---
            if (isGoal) {
                const defendingMpId = resolveDefendingGkMatchPlayerId(
                    event,
                    bundle.detail.homeTeamId,
                    bundle.detail.awayTeamId
                );
                const defendingRow = defendingMpId != null ? lineup.get(defendingMpId) : undefined;
                const defendingTeamId =
                    defendingRow?.teamId ??
                    (event.shot.teamId === bundle.detail.homeTeamId
                        ? bundle.detail.awayTeamId
                        : bundle.detail.homeTeamId);

                const usesNew =
                    event.shootoutOrder != null || (defendingTeamId != null && newTracking.get(defendingTeamId) === true);

                if (usesNew && defendingRow) {
                    credit(defendingRow, defendingMpId, 'goal', goalZone, poolZone);
                    return;
                }

                // Legacy: last saver for defending team, else pending.
                const lastSaverId = lastSaverByTeam.get(defendingTeamId);
                if (lastSaverId != null) {
                    const lastSaver = lineup.get(lastSaverId);
                    if (lastSaver) {
                        credit(lastSaver, lastSaverId, 'goal', goalZone, poolZone);
                        return;
                    }
                }

                const pending = pendingGoalsByTeam.get(defendingTeamId) || [];
                pending.push({ goalZone, poolZone });
                pendingGoalsByTeam.set(defendingTeamId, pending);
                return;
            }

            // Blocks / misses: not GK attempts (TW).
        });
    });

    const keeperRows = Array.from(keepers.values())
        .map(finalizeKeeper)
        .sort((a, b) => b.saves - a.saves || b.sog - a.sog || a.name.localeCompare(b.name));

    return {
        teamAgainst,
        teamAgainstPool,
        keepers: keeperRows,
        located,
        total,
        offTarget,
        noTarget,
    };
};

export const collectGkZoneStatsForPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    options: Omit<CollectGkZoneOptions, 'playerId'> = {}
): GkZoneStats => collectGkZoneStats(bundles, ourTeamIds, { ...options, playerId });

type AttributedShot = {
    event: EventLightVm;
    isGoal: boolean;
    bundleId: number;
};

/** Shared attribution walk for goal-target and pool-location scatters. */
const attributedShotsForPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    options: GkMapOptions = {}
): AttributedShot[] => {
    const attributed: AttributedShot[] = [];

    bundles.forEach((bundle) => {
        const lineup = indexLineup(bundle);
        const newTracking = detectNewGkTrackingByTeam(bundle.events);
        const lastSaverByTeam = new Map<number, number>();

        sortEventsChronological(bundle.events).forEach((event: EventLightVm) => {
            if (!event.shot) return;
            if (ourTeamIds.includes(event.shot.teamId)) return;
            if (!matchesPeriodFilter(event, options.periodKeys)) return;
            if (!shotPassesFilters(event.shot, options)) return;

            const isGoal = !!event.shot.isGoal;
            const savedById = event.shot.savedById;
            let attributedMpId: number | undefined;

            if (!isGoal && savedById != null) {
                attributedMpId = savedById;
                const saver = lineup.get(savedById);
                if (saver) lastSaverByTeam.set(saver.teamId, savedById);
            } else if (isGoal) {
                const defendingMpId = resolveDefendingGkMatchPlayerId(
                    event,
                    bundle.detail.homeTeamId,
                    bundle.detail.awayTeamId
                );
                const defendingRow = defendingMpId != null ? lineup.get(defendingMpId) : undefined;
                const defendingTeamId =
                    defendingRow?.teamId ??
                    (event.shot.teamId === bundle.detail.homeTeamId
                        ? bundle.detail.awayTeamId
                        : bundle.detail.homeTeamId);
                const usesNew =
                    event.shootoutOrder != null || (defendingTeamId != null && newTracking.get(defendingTeamId) === true);
                if (usesNew && defendingMpId != null) {
                    attributedMpId = defendingMpId;
                } else {
                    attributedMpId = lastSaverByTeam.get(defendingTeamId);
                }
            } else {
                return;
            }

            if (attributedMpId == null) return;
            const row = lineup.get(attributedMpId);
            if (!row || row.playerId !== playerId) return;

            attributed.push({ event, isGoal, bundleId: bundle.detail.id });
        });
    });

    return attributed;
};

/**
 * Goal-target scatter for attempts this keeper faced (saves + goals only).
 */
export const mapTargetsFacedByPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    options: GkMapOptions = {}
): MapSide => {
    const side = emptyMapSide();
    const byId = new Map(bundles.map((b) => [b.detail.id, b]));

    attributedShotsForPlayer(bundles, ourTeamIds, playerId, options).forEach((item) => {
        const shot = item.event.shot!;
        side.total += 1;
        const x = shot.targetX;
        const y = shot.targetY;
        if (x == null || y == null || x < 0 || x > 1 || y < 0 || y > 1) return;
        const bundle = byId.get(item.bundleId);
        if (!bundle) return;
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        const ctx = mapPointContext(
            bundle.detail,
            item.event,
            item.isGoal ? 'Goal against' : 'Save',
            names,
            scoreAt.get(item.event.id),
            finalScore
        );
        side.points.push({
            key: `${item.bundleId}-${item.event.id}-t`,
            x,
            y,
            outcome: item.isGoal ? 'goal' : 'saved',
            ...ctx,
        });
    });

    return side;
};

/**
 * Pool-location scatter for attempts this keeper faced (saves + goals only).
 */
export const mapLocationsFacedByPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    options: GkMapOptions = {}
): MapSide => {
    const side = emptyMapSide();
    const byId = new Map(bundles.map((b) => [b.detail.id, b]));

    attributedShotsForPlayer(bundles, ourTeamIds, playerId, options).forEach((item) => {
        const shot = item.event.shot!;
        side.total += 1;
        const x = shot.locationX;
        const y = shot.locationY;
        if (x == null || y == null || x < 0 || x > 1 || y < 0 || y > 1) return;
        const bundle = byId.get(item.bundleId);
        if (!bundle) return;
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        const ctx = mapPointContext(
            bundle.detail,
            item.event,
            item.isGoal ? 'Goal against' : 'Save',
            names,
            scoreAt.get(item.event.id),
            finalScore
        );
        side.points.push({
            key: `${item.bundleId}-${item.event.id}-l`,
            x,
            y,
            outcome: item.isGoal ? 'goal' : 'saved',
            ...ctx,
        });
    });

    return side;
};

export interface GkSituationView {
    situation: GkSituation;
    label: string;
    stats: GkZoneStats;
    goalMap: MapSide;
    poolMap: MapSide;
}

export const buildGkSituationViews = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number
): GkSituationView[] => {
    const order: GkSituation[] = ['extraPlayer', 'sixOnSix', 'penalty', 'all'];
    return order.map((situation) => {
        const options: GkMapOptions = { situation };
        return {
            situation,
            label: GK_SITUATION_LABELS[situation],
            stats: collectGkZoneStatsForPlayer(bundles, ourTeamIds, playerId, { situation }),
            goalMap: mapTargetsFacedByPlayer(bundles, ourTeamIds, playerId, options),
            poolMap: mapLocationsFacedByPlayer(bundles, ourTeamIds, playerId, options),
        };
    });
};
